/**
 * The dialogs a hub opens onto, shared by its Monitoring card and the Hubs
 * page: its settings, its pictures, its incidents.
 */
import { useState } from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { HubSettingsSource } from '@/services/field.service';
import HubDetails from './HubDetails';
import HubImageViewer from './HubImageViewer';
import HubIncidents from './HubIncidents';

function Title({ children, mac }: { children: string; mac: string }) {
    return (
        <DialogTitle className="text-white">
            {children}{' '}
            <span className="font-mono text-sm text-[#a8b0bb] ml-2">{mac}</span>
        </DialogTitle>
    );
}

export function HubSettingsDialog({
    hub,
    open,
    onClose,
}: {
    hub: HubSettingsSource;
    open: boolean;
    onClose: () => void;
}) {
    // A settings dialog with unsaved edits does not close on Escape, an outside
    // click or its cross: an edit to a hub's Hub.json is either saved or
    // cancelled, never dropped by a stray click. The nudge says so when one is
    // refused.
    const [dirty, setDirty] = useState(false);
    const [nudge, setNudge] = useState(false);
    const close = () => {
        onClose();
        setDirty(false);
        setNudge(false);
    };

    return (
        <Dialog
            open={open}
            onOpenChange={(next) => {
                if (next) return;
                if (dirty) setNudge(true);
                else close();
            }}
        >
            <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto bg-[#161c22] border-[#1e242a] text-[#d8dde2]">
                <DialogHeader>
                    <Title mac={hub.macAddress}>Hub settings</Title>
                </DialogHeader>
                {nudge && dirty && (
                    <p className="text-sm text-[#ff8a84]">
                        Unsaved changes — save or cancel them first.
                    </p>
                )}
                {/* Remounts each time, so a previous visit's half-typed draft
                    and its error are gone. */}
                {open && (
                    <HubDetails
                        hub={hub}
                        onClose={close}
                        onDirtyChange={setDirty}
                    />
                )}
            </DialogContent>
        </Dialog>
    );
}

export function HubPicturesDialog({
    macAddress,
    open,
    onOpenChange,
}: {
    macAddress: string;
    open: boolean;
    onOpenChange: (open: boolean) => void;
}) {
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-none w-[95vw] h-[92vh] flex flex-col p-4 bg-[#161c22] border-[#1e242a] text-[#d8dde2]">
                <DialogHeader className="flex-shrink-0">
                    <Title mac={macAddress}>Hub pictures</Title>
                </DialogHeader>
                {open && <HubImageViewer macAddress={macAddress} />}
            </DialogContent>
        </Dialog>
    );
}

export function HubIncidentsDialog({
    macAddress,
    open,
    onOpenChange,
}: {
    macAddress: string;
    open: boolean;
    onOpenChange: (open: boolean) => void;
}) {
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto bg-[#161c22] border-[#1e242a] text-[#d8dde2]">
                <DialogHeader>
                    <Title mac={macAddress}>Hub incidents</Title>
                </DialogHeader>
                {open && <HubIncidents macAddress={macAddress} />}
            </DialogContent>
        </Dialog>
    );
}
